// src/modulos/Cadastros/ShippingPage.js

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './Registers.module.css';
import { FiEdit, FiTrash2, FiMapPin } from 'react-icons/fi';
import { FaPhoneAlt } from 'react-icons/fa';
import { HiOutlineIdentification } from 'react-icons/hi';

// Common Components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
    Toast,
} from '../../components/common';

import useToast from '../../hooks/useToast';
import usePagination from '../../hooks/usePagination';

// 1. Definições das Coleções
const CARRIERS_COLLECTION = 'transportadoras';
const ADDRESS_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-endereco'
};

const DEFAULT_CARRIER = {
    codigo: '',
    tipoPessoa: 'Jurídica',
    cnpjCpf: '',
    inscricaoEstadual: '',
    rntrc: '',
    razaoSocial: '',
    nomeFantasia: '',
    email: '',
    telefone: '',
    contatoNome: '',
    tipoEndereco: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    observacoes: '',
    altura: '',
    largura: '',
    comprimento: '',
    cargaMaxima: '',
    situacao: 'Ativo'
};

const ITEMS_PER_PAGE = 10;

const ShippingPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('transportadoras');
    const { toasts, addToast, removeToast } = useToast();

    // Hooks de dados
    const { data: carriers, loading, formLoading, addDocument, updateDocument, deleteDocument, refreshData } = useFirestore(CARRIERS_COLLECTION, 'codigo');

    // Estado para controlar se deve carregar os tipos de endereço
    const [shouldLoadAddressTypes, setShouldLoadAddressTypes] = useState(false);

    // Busca os tipos de endereço APENAS quando o modal estiver aberto
    const { data: addressTypes } = useSubCollection(
        shouldLoadAddressTypes ? ADDRESS_TYPE_COLLECTION_PATH.parentCollection : null,
        shouldLoadAddressTypes ? ADDRESS_TYPE_COLLECTION_PATH.parentId : null,
        shouldLoadAddressTypes ? ADDRESS_TYPE_COLLECTION_PATH.subCollectionName : null,
        'name'
    );

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_CARRIER);
    const [originalFormData, setOriginalFormData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const submitInProgress = useRef(false);

    // Gerador de código (Ex: T001)
    const generateNextCode = useCallback(() => {
        const prefix = 'T';
        if (!carriers || carriers.length === 0) return `${prefix}001`;
        const codes = carriers
            .map(c => parseInt(c.codigo?.replace(prefix, ''), 10))
            .filter(n => !isNaN(n));
        const next = codes.length > 0 ? Math.max(...codes) + 1 : 1;
        return `${prefix}${next.toString().padStart(3, '0')}`;
    }, [carriers]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData(DEFAULT_CARRIER);
        setOriginalFormData(null);
        setShouldLoadAddressTypes(false);
        submitInProgress.current = false;
    };

    const handleNewClick = () => {
        if (!can.create) {
            addToast(t('pages.shipping.noPermissionCreate'), 'error');
            return;
        }

        setEditingItem(null);
        const newCode = generateNextCode();
        const newFormData = { ...DEFAULT_CARRIER, codigo: newCode };
        setFormData(newFormData);
        setOriginalFormData(null);
        setShouldLoadAddressTypes(true);
        setIsModalOpen(true);
    };

    const handleEditClick = (item) => {
        if (!can.edit) {
            addToast(t('pages.shipping.noPermissionEdit'), 'error');
            return;
        }

        setEditingItem(item);
        setFormData(item);
        setOriginalFormData(JSON.parse(JSON.stringify(item)));
        setShouldLoadAddressTypes(true);
        setIsModalOpen(true);
    };

    // Dirty checking: verifica se houve alteração real nos dados
    const hasChanges = useCallback(() => {
        if (!editingItem) return true;
        if (!originalFormData) return true;
        return JSON.stringify(formData) !== JSON.stringify(originalFormData);
    }, [formData, originalFormData, editingItem]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (submitInProgress.current || formLoading) {
            return;
        }

        if (!hasChanges()) {
            resetForm();
            return;
        }

        submitInProgress.current = true;

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, formData);
                if (result.success) {
                    addToast(
                        t('pages.shipping.updateSuccess', { name: formData.razaoSocial }),
                        'success'
                    );
                    resetForm();
                } else {
                    addToast(t('pages.shipping.updateError', { error: result.error }), 'error');
                }
            } else {
                result = await addDocument(formData);
                if (result.success) {
                    addToast(
                        t('pages.shipping.createSuccess', { name: formData.razaoSocial }),
                        'success'
                    );
                    resetForm();
                } else {
                    addToast(t('pages.shipping.createError', { error: result.error }), 'error');
                }
            }
        } catch (error) {
            console.error('Erro ao salvar:', error);
            addToast(t('pages.shipping.updateError', { error: error.message }), 'error');
        }

        submitInProgress.current = false;
    };

    // Função segura para deletar
    const handleDeleteClick = (id, name) => {
        if (!can.delete) {
            addToast(t('pages.shipping.noPermissionDelete'), 'error');
            return;
        }
        setDeleteConfirmId(id);
    };

    const confirmDelete = async () => {
        if (!deleteConfirmId) return;

        try {
            const result = await deleteDocument(deleteConfirmId);
            if (result.success) {
                addToast(t('pages.shipping.deleteSuccess'), 'success');
                setDeleteConfirmId(null);
            } else {
                addToast(t('pages.shipping.deleteError'), 'error');
            }
        } catch (error) {
            addToast(t('pages.shipping.deleteError'), 'error');
        }
    };

    const cancelDelete = () => {
        setDeleteConfirmId(null);
    };

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
    }, []);

    // Busca local multicampos otimizada
    const filteredCarriers = useMemo(() => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return carriers || [];

        return (carriers || []).filter(c =>
            c.razaoSocial?.toLowerCase().includes(term) ||
            c.nomeFantasia?.toLowerCase().includes(term) ||
            c.cidade?.toLowerCase().includes(term) ||
            c.uf?.toLowerCase().includes(term) ||
            c.cnpjCpf?.includes(term) ||
            c.codigo?.toLowerCase().includes(term)
        );
    }, [carriers, searchTerm]);

    const {
        currentPage,
        totalPages,
        currentItems: currentCarriers,
        goToPage,
        resetPage,
    } = usePagination(filteredCarriers, ITEMS_PER_PAGE);

    // Reset page when search changes
    useEffect(() => {
        resetPage();
    }, [searchTerm, resetPage]);

    // --- Modal Content ---
    const renderModalContent = () => {
        const isLoading = formLoading || submitInProgress.current;

        return (
            <>
                {/* Seção 1: Identificação */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><HiOutlineIdentification /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.shipping.form.identification')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.code')} <span className={styles.required}>*</span></label>
                            <input
                                name="codigo"
                                value={formData.codigo}
                                readOnly
                                className={styles.input}
                                style={{ backgroundColor: '#f0f0f0' }}
                            />
                            <small className={styles.fieldHint}>{t('pages.shipping.form.codeHint')}</small>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.document')}</label>
                            <input
                                name="cnpjCpf"
                                value={formData.cnpjCpf}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder="00.000.000/0000-00"
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.stateRegistration')}</label>
                            <input
                                name="inscricaoEstadual"
                                value={formData.inscricaoEstadual}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.stateRegistrationPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            <label>{t('pages.shipping.form.companyName')} <span className={styles.required}>*</span></label>
                            <input
                                name="razaoSocial"
                                value={formData.razaoSocial}
                                onChange={handleChange}
                                required
                                className={styles.input}
                                placeholder={t('pages.shipping.form.companyNamePlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.tradeName')}</label>
                            <input
                                name="nomeFantasia"
                                value={formData.nomeFantasia}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.tradeNamePlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>

                {/* Seção 2: Endereço */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiMapPin /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.shipping.form.address')}</h3>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.addressType')}</label>
                            <select
                                name="tipoEndereco"
                                value={formData.tipoEndereco}
                                onChange={handleChange}
                                className={styles.select}
                                disabled={isLoading}
                            >
                                <option value="">{t('common.select')}</option>
                                {addressTypes?.map(t => (
                                    <option key={t.id} value={t.name}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.zipCode')}</label>
                            <input
                                name="cep"
                                value={formData.cep}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder="00000-000"
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            <label>{t('pages.shipping.form.street')}</label>
                            <input
                                name="logradouro"
                                value={formData.logradouro}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.streetPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.number')}</label>
                            <input
                                name="numero"
                                value={formData.numero}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.numberPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.complement')}</label>
                            <input
                                name="complemento"
                                value={formData.complemento}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.complementPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.neighborhood')}</label>
                            <input
                                name="bairro"
                                value={formData.bairro}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.neighborhoodPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.city')}</label>
                            <input
                                name="cidade"
                                value={formData.cidade}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.cityPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.state')}</label>
                            <input
                                name="uf"
                                value={formData.uf}
                                onChange={handleChange}
                                className={styles.input}
                                maxLength="2"
                                placeholder={t('pages.shipping.form.statePlaceholder')}
                                style={{ textTransform: 'uppercase' }}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>

                {/* Seção 3: Contato e Capacidade */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaPhoneAlt /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.shipping.form.contactAndCapacity')}</h3>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.contactName')}</label>
                            <input
                                name="contatoNome"
                                value={formData.contatoNome}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.contactNamePlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.phone')}</label>
                            <input
                                name="telefone"
                                value={formData.telefone}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.phonePlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.email')}</label>
                            <input
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.emailPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.rntrc')}</label>
                            <input
                                name="rntrc"
                                value={formData.rntrc}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.shipping.form.rntrcPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className={styles.formGrid} style={{ marginTop: '16px' }}>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.height')}</label>
                            <input
                                name="altura"
                                type="number"
                                step="0.01"
                                value={formData.altura}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder="0,00"
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.width')}</label>
                            <input
                                name="largura"
                                type="number"
                                step="0.01"
                                value={formData.largura}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder="0,00"
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.length')}</label>
                            <input
                                name="comprimento"
                                type="number"
                                step="0.01"
                                value={formData.comprimento}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder="0,00"
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.shipping.form.maxLoad')}</label>
                            <input
                                name="cargaMaxima"
                                type="number"
                                step="0.01"
                                value={formData.cargaMaxima}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder="0,00"
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className={styles.inputGroup} style={{ marginTop: '16px' }}>
                        <label>{t('pages.shipping.form.observations')}</label>
                        <textarea
                            name="observacoes"
                            value={formData.observacoes}
                            onChange={handleChange}
                            className={styles.textarea}
                            rows="3"
                            placeholder={t('pages.shipping.form.observationsPlaceholder')}
                            disabled={isLoading}
                        />
                    </div>
                </div>
            </>
        );
    };

    // --- Table Render ---
    const renderTable = () => {
        if (loading) {
            return <TableSkeleton columns={5} rows={ITEMS_PER_PAGE} />;
        }

        if (currentCarriers.length === 0) {
            return (
                <EmptyState
                    icon="🚚"
                    title={searchTerm ? t('pages.shipping.table.emptySearch') : t('pages.shipping.table.empty')}
                    message={searchTerm ? t('pages.shipping.search.noResults') : t('pages.shipping.table.emptyMessage')}
                    actionText={can.create ? t('pages.shipping.newCarrier') : undefined}
                    onAction={can.create ? handleNewClick : undefined}
                />
            );
        }

        return (
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th style={{ width: '80px' }}>{t('pages.shipping.table.code')}</th>
                        <th>{t('pages.shipping.table.companyName')}</th>
                        <th style={{ width: '150px' }}>{t('pages.shipping.table.document')}</th>
                        <th style={{ width: '150px' }}>{t('pages.shipping.table.cityState')}</th>
                        <th style={{ width: '120px' }}>{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentCarriers.map(item => (
                        <tr key={item.id}>
                            <td>{item.codigo}</td>
                            <td>
                                <strong>{item.razaoSocial}</strong>
                                {item.nomeFantasia && <div style={{ fontSize: '0.8rem', color: '#666' }}>{item.nomeFantasia}</div>}
                            </td>
                            <td>{item.cnpjCpf}</td>
                            <td>{item.cidade} / {item.uf}</td>
                            <td>
                                <div className={styles.actionsContainer}>
                                    {can.edit && (
                                        <button
                                            onClick={() => handleEditClick(item)}
                                            className={styles.actionButton}
                                            title={t('common.edit')}
                                            disabled={loading}
                                        >
                                            <FiEdit />
                                        </button>
                                    )}
                                    {can.delete && (
                                        <button
                                            onClick={() => handleDeleteClick(item.id, item.razaoSocial)}
                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                            title={t('common.delete')}
                                            disabled={loading}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    )}
                                    {!can.edit && !can.delete && (
                                        <span className={styles.textMuted}>{t('common.noActionsAvailable')}</span>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div className={styles.pageContainer}>
            {/* Toast Notifications */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </div>
            )}

            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={resetForm}
                onSubmit={handleSubmit}
                title={editingItem ? t('pages.shipping.editCarrier') : t('pages.shipping.newCarrier')}
                subtitle={editingItem ? `${t('pages.shipping.editingLabel')}: ${formData.razaoSocial || editingItem.razaoSocial} (${formData.codigo})` : undefined}
                isLoading={formLoading || submitInProgress.current}
                submitText={editingItem ? t('common.saveChanges') : t('pages.shipping.form.create')}
                maxWidth="860px"
            >
                {renderModalContent()}
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={!!deleteConfirmId}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title={t('pages.shipping.deleteConfirmTitle')}
                message={t('pages.shipping.deleteConfirmMessage')}
                confirmText={t('pages.shipping.deleteConfirmButton')}
                cancelText={t('common.cancel')}
                isLoading={formLoading || submitInProgress.current}
                danger
            >
                <p style={{ fontSize: '0.85rem', color: 'var(--danger-color)', marginTop: '8px' }}>
                    ⚠️ {t('pages.shipping.deleteConfirmWarning')}
                </p>
            </ConfirmModal>

            {/* Page Header */}
            <PageHeader
                title={t('pages.shipping.title')}
                actionText={can.create ? t('pages.shipping.newCarrier') : undefined}
                onAction={handleNewClick}
                disabled={loading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <SearchBar
                placeholder={t('pages.shipping.search.placeholder')}
                onSearch={handleSearch}
                disabled={loading}
                debounceDelay={300}
                initialValue={searchTerm}
            />

            {/* Table */}
            <div className={styles.tableContainer}>
                {renderTable()}
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredCarriers.length}
                onPageChange={goToPage}
                disabled={loading}
                itemsLabel={t('pages.shipping.table.items')}
            />
        </div>
    );
};

export default ShippingPage;